import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, post, del, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

/** Role assignment row (welcome video for a role). */
export interface Video {
  video_id: string;
  library_video_id?: string | null;
  topic_id?: string | null;
  topic_label?: string | null;
  topic_order?: number | null;
  topic_is_active?: boolean | null;
  title: string;
  video_url: string;
  description?: string;
  role: string;
  is_active?: boolean;
  video_order?: number;
  created_at: string;
  updated_at?: string;
}

/** Shared catalog video (library). */
export interface LibraryVideo {
  id: string;
  video_url: string;
  title: string;
  description?: string | null;
  topic_id?: string | null;
  topic_label?: string | null;
  /** 1-based default catalog order within the topic. */
  sort_order: number;
  assigned_roles: string[];
  created_at: string;
  updated_at?: string;
}

export interface VideoTopic {
  id: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  video_count: number;
}

export interface LibraryVideoPayload {
  video_url: string;
  title: string;
  description?: string;
  topic_id: string | null;
  /** 1-based insert position within the topic; omit to append. */
  sort_order?: number;
}

interface VideoState {
  /** Role assignment rows (admin assignments + metadata). */
  videos: Video[];
  /** Home-page feed for the current user. */
  userVideos: Video[];
  /** Catalog library videos. */
  libraryVideos: LibraryVideo[];
  topics: VideoTopic[];
  loading: boolean;
  userVideosLoading: boolean;
  libraryLoading: boolean;
  topicsLoading: boolean;
  librarySaving: boolean;
  deleteVideoLoading: boolean;
  reorderVideosLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
}

let latestAdminVideosFetchId = 0;

const initialState: VideoState = {
  videos: [],
  userVideos: [],
  libraryVideos: [],
  topics: [],
  loading: false,
  userVideosLoading: false,
  libraryLoading: false,
  topicsLoading: false,
  librarySaving: false,
  deleteVideoLoading: false,
  reorderVideosLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
};

const getVideoErrorMessage = (error: any, fallback: string): string =>
  error.response?.data?.response?.message ||
  error.response?.data?.message ||
  error.response?.data?.detail ||
  fallback;

const mapVideoItem = (item: any): Video => ({
  ...item,
  video_id: item.video_id || item.id,
  video_order: item.video_order ?? -1,
});

/** Unwrap common API envelope (`{ content, response }`) or pass through legacy bodies. */
const unwrapApiContent = <T = any>(payload: any): T =>
  (payload?.content ?? payload) as T;

/**
 * Mutating library/topic call: toast, refresh dependents, reject with message.
 */
const runLibraryMutation = async <T>(
  request: () => Promise<T>,
  options: {
    dispatch: any;
    rejectWithValue: (value: string) => any;
    success: string;
    failure: string;
    refresh: Array<"library" | "topics" | "assignments">;
    quiet?: boolean;
  }
) => {
  try {
    const response = unwrapApiContent(await request());
    const { dispatch, refresh } = options;
    await Promise.all(
      [
        refresh.includes("library") && dispatch(fetchLibraryVideos()),
        refresh.includes("topics") && dispatch(fetchVideoTopics()),
        refresh.includes("assignments") && dispatch(fetchAdminVideos({})),
      ].filter(Boolean)
    );
    if (!options.quiet) toast.success(options.success);
    return response;
  } catch (error: any) {
    const message = getVideoErrorMessage(error, options.failure);
    toast.error(message);
    return options.rejectWithValue(message);
  }
};

// ——— Assignments / home ———

export const fetchAdminVideos = createAsyncThunk(
  "video/fetchAdminVideos",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      role?: string;
    } = {},
    { rejectWithValue }
  ) => {
    const fetchId = ++latestAdminVideosFetchId;
    try {
      const response = await get(API_URLS.fetchAdminVideos(params));
      return { fetchId, data: response };
    } catch (error: any) {
      return rejectWithValue(
        getVideoErrorMessage(error, "Failed to fetch videos")
      );
    }
  }
);

export const fetchUserVideos = createAsyncThunk(
  "video/fetchUserVideos",
  async (params: { role?: string } = {}, { rejectWithValue }) => {
    try {
      return await get(API_URLS.fetchUserVideos(params));
    } catch (error: any) {
      return rejectWithValue(
        getVideoErrorMessage(error, "Failed to fetch videos")
      );
    }
  }
);

export const updateVideoActiveStatus = createAsyncThunk(
  "video/updateVideoActiveStatus",
  async (
    {
      video_id,
      is_active,
      quiet = false,
    }: { video_id: string; is_active: boolean; quiet?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateAdminVideo(), {
        video_id,
        is_active,
      });
      if (!quiet) {
        toast.success(
          is_active ? "Video set to active" : "Video set to inactive"
        );
      }
      return {
        video_id,
        is_active,
        response: unwrapApiContent(response),
      };
    } catch (error: any) {
      const errorMessage = getVideoErrorMessage(
        error,
        "Failed to update video status"
      );
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

type ReorderVideosPayload = { items?: Video[]; videos?: Video[] };

export const reorderAdminVideos = createAsyncThunk(
  "video/reorderAdminVideos",
  async (
    payload: { role: string; video_ids: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateAdminVideo(), payload);
      return unwrapApiContent<ReorderVideosPayload>(response);
    } catch (error: any) {
      const errorMessage = getVideoErrorMessage(
        error,
        "Failed to save video order"
      );
      toast.error(errorMessage);
      return rejectWithValue(errorMessage) as never;
    }
  }
);

export const deleteVideo = createAsyncThunk(
  "video/deleteVideo",
  async (videoId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteAdminVideo(videoId));
      return videoId;
    } catch (error: any) {
      return rejectWithValue(
        getVideoErrorMessage(error, "Failed to delete video")
      );
    }
  }
);

// ——— Library / topics ———

type LibraryListPayload = { items: LibraryVideo[]; total?: number };
type TopicListPayload = { items: VideoTopic[]; total?: number };

export const fetchLibraryVideos = createAsyncThunk(
  "video/fetchLibraryVideos",
  async (_arg: void, { rejectWithValue }) => {
    try {
      return unwrapApiContent<LibraryListPayload>(
        await get(API_URLS.fetchLibraryVideos())
      );
    } catch (error: any) {
      return rejectWithValue(
        getVideoErrorMessage(error, "Failed to fetch video library")
      ) as never;
    }
  }
);

export const fetchVideoTopics = createAsyncThunk(
  "video/fetchVideoTopics",
  async (_arg: void, { rejectWithValue }) => {
    try {
      return unwrapApiContent<TopicListPayload>(
        await get(API_URLS.fetchVideoTopics())
      );
    } catch (error: any) {
      return rejectWithValue(
        getVideoErrorMessage(error, "Failed to fetch topics")
      ) as never;
    }
  }
);

export const createLibraryVideo = createAsyncThunk(
  "video/createLibraryVideo",
  (payload: LibraryVideoPayload, { dispatch, rejectWithValue }) =>
    runLibraryMutation(() => post(API_URLS.createLibraryVideo(), payload), {
      dispatch,
      rejectWithValue,
      success: "Video added to library",
      failure: "Failed to add video",
      refresh: ["library", "topics"],
    })
);

export const updateLibraryVideo = createAsyncThunk(
  "video/updateLibraryVideo",
  (
    { id, ...payload }: LibraryVideoPayload & { id: string },
    { dispatch, rejectWithValue }
  ) =>
    runLibraryMutation(() => put(API_URLS.updateLibraryVideo(id), payload), {
      dispatch,
      rejectWithValue,
      success: "Video updated for every assigned role",
      failure: "Failed to update video",
      refresh: ["library", "topics", "assignments"],
    })
);

export const removeLibraryVideo = createAsyncThunk(
  "video/removeLibraryVideo",
  (id: string, { dispatch, rejectWithValue }) =>
    runLibraryMutation(() => del(API_URLS.deleteLibraryVideo(id)), {
      dispatch,
      rejectWithValue,
      success: "Video deleted from library and all roles",
      failure: "Failed to delete video",
      refresh: ["library", "topics", "assignments"],
    })
);

export const assignLibraryVideos = createAsyncThunk(
  "video/assignLibraryVideos",
  async (
    payload: {
      role: string | string[];
      library_video_ids: string[];
      is_active: boolean;
      /** Skip success toast + refresh (caller shows one summary). */
      quiet?: boolean;
    },
    { dispatch, rejectWithValue }
  ) => {
    const { quiet = false, ...assignPayload } = payload;
    const roles = (
      Array.isArray(assignPayload.role)
        ? assignPayload.role
        : [assignPayload.role]
    ).filter(Boolean);
    if (roles.length === 0) {
      return rejectWithValue("No roles to assign") as never;
    }

    // One request for all roles — backend assigns each subrole in a single txn.
    const body =
      roles.length === 1
        ? {
            role: roles[0],
            library_video_ids: assignPayload.library_video_ids,
            is_active: assignPayload.is_active,
          }
        : {
            roles,
            library_video_ids: assignPayload.library_video_ids,
            is_active: assignPayload.is_active,
          };

    return runLibraryMutation(
      () => post(API_URLS.assignLibraryVideos(), body),
      {
        dispatch,
        rejectWithValue,
        success:
          roles.length > 1
            ? "Videos assigned to all account team roles"
            : "Videos assigned",
        failure: "Failed to assign videos",
        refresh: quiet ? [] : ["library", "assignments"],
        quiet,
      }
    );
  }
);

export const createVideoTopic = createAsyncThunk(
  "video/createVideoTopic",
  (label: string, { dispatch, rejectWithValue }) =>
    runLibraryMutation(() => post(API_URLS.createVideoTopic(), { label }), {
      dispatch,
      rejectWithValue,
      success: `Topic "${label}" created`,
      failure: "Failed to create topic",
      refresh: ["topics"],
    })
);

export const renameVideoTopic = createAsyncThunk(
  "video/renameVideoTopic",
  (
    { id, label }: { id: string; label: string },
    { dispatch, rejectWithValue }
  ) =>
    runLibraryMutation(() => put(API_URLS.updateVideoTopic(id), { label }), {
      dispatch,
      rejectWithValue,
      success: "Topic renamed",
      failure: "Failed to rename topic",
      refresh: ["topics", "library", "assignments"],
    })
);

export const setVideoTopicActive = createAsyncThunk(
  "video/setVideoTopicActive",
  (
    { id, isActive }: { id: string; isActive: boolean },
    { dispatch, rejectWithValue }
  ) =>
    runLibraryMutation(
      () => put(API_URLS.updateVideoTopic(id), { is_active: isActive }),
      {
        dispatch,
        rejectWithValue,
        success: isActive
          ? "Topic activated — its videos are visible to assigned roles"
          : "Topic deactivated — its videos are hidden from users",
        failure: "Failed to update topic status",
        refresh: ["topics", "assignments"],
      }
    )
);

export const reorderVideoTopics = createAsyncThunk(
  "video/reorderVideoTopics",
  (topicIds: string[], { dispatch, rejectWithValue }) =>
    runLibraryMutation(
      () => put(API_URLS.reorderVideoTopics(), { topic_ids: topicIds }),
      {
        dispatch,
        rejectWithValue,
        success: "Topic order saved",
        failure: "Failed to save topic order",
        refresh: ["topics", "assignments"],
      }
    )
);

export const reorderLibraryVideos = createAsyncThunk(
  "video/reorderLibraryVideos",
  (
    payload: {
      topic_id: string | null;
      library_video_ids: string[];
      quiet?: boolean;
    },
    { dispatch, rejectWithValue }
  ) => {
    const { quiet, ...body } = payload;
    return runLibraryMutation(
      () => put(API_URLS.reorderLibraryVideos(), body),
      {
        dispatch,
        rejectWithValue,
        success: "Library order saved",
        failure: "Failed to save library order",
        refresh: quiet ? [] : ["library"],
        quiet,
      }
    );
  }
);

export const deleteVideoTopic = createAsyncThunk(
  "video/deleteVideoTopic",
  (id: string, { dispatch, rejectWithValue }) =>
    runLibraryMutation(() => del(API_URLS.deleteVideoTopic(id)), {
      dispatch,
      rejectWithValue,
      success: "Topic deleted; its videos are now uncategorized",
      failure: "Failed to delete topic",
      refresh: ["topics", "library", "assignments"],
    })
);

const LIBRARY_MUTATIONS = [
  createLibraryVideo,
  updateLibraryVideo,
  removeLibraryVideo,
  assignLibraryVideos,
  createVideoTopic,
  renameVideoTopic,
  setVideoTopicActive,
  reorderVideoTopics,
  reorderLibraryVideos,
  deleteVideoTopic,
];

const videoSlice = createSlice({
  name: "video",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAdminVideos.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminVideos.fulfilled, (state, action) => {
        if (action.payload.fetchId !== latestAdminVideosFetchId) return;
        state.loading = false;
        const payload = action.payload.data;
        const rawItems = payload.items || payload.videos || [];
        state.videos = rawItems.map(mapVideoItem);
        state.totalCount = payload.total || 0;
        state.currentPage = payload.page || 1;
      })
      .addCase(fetchAdminVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserVideos.pending, state => {
        state.userVideosLoading = true;
        state.error = null;
      })
      .addCase(fetchUserVideos.fulfilled, (state, action) => {
        state.userVideosLoading = false;
        const rawItems = action.payload.items || action.payload.videos || [];
        state.userVideos = rawItems.map(mapVideoItem);
      })
      .addCase(fetchUserVideos.rejected, (state, action) => {
        state.userVideosLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateVideoActiveStatus.pending, state => {
        state.error = null;
      })
      .addCase(updateVideoActiveStatus.fulfilled, (state, action) => {
        const updated = mapVideoItem(action.payload.response);
        const index = state.videos.findIndex(
          video => video.video_id === updated.video_id
        );
        if (index !== -1) {
          state.videos[index] = { ...state.videos[index], ...updated };
        }
      })
      .addCase(updateVideoActiveStatus.rejected, (state, action) => {
        state.error = (action.payload as string) || null;
      })
      .addCase(reorderAdminVideos.pending, state => {
        state.reorderVideosLoading = true;
      })
      .addCase(reorderAdminVideos.fulfilled, (state, action) => {
        state.reorderVideosLoading = false;
        const items = action.payload?.items || [];
        if (!items.length) return;

        const reorderedVideos = items.map(mapVideoItem);
        const roleKey = reorderedVideos[0]?.role;
        const otherVideos = state.videos.filter(
          video => video.role !== roleKey
        );
        state.videos = [...otherVideos, ...reorderedVideos];
      })
      .addCase(reorderAdminVideos.rejected, state => {
        state.reorderVideosLoading = false;
      })
      .addCase(deleteVideo.pending, state => {
        state.deleteVideoLoading = true;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.deleteVideoLoading = false;
        state.videos = state.videos.filter(
          video => video.video_id !== action.payload
        );
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteVideo.rejected, state => {
        state.deleteVideoLoading = false;
      })
      .addCase(fetchLibraryVideos.pending, state => {
        state.libraryLoading = true;
      })
      .addCase(fetchLibraryVideos.fulfilled, (state, action) => {
        state.libraryLoading = false;
        state.libraryVideos = action.payload?.items || [];
      })
      .addCase(fetchLibraryVideos.rejected, state => {
        state.libraryLoading = false;
      })
      .addCase(fetchVideoTopics.pending, state => {
        state.topicsLoading = true;
      })
      .addCase(fetchVideoTopics.fulfilled, (state, action) => {
        state.topicsLoading = false;
        state.topics = action.payload?.items || [];
      })
      .addCase(fetchVideoTopics.rejected, state => {
        state.topicsLoading = false;
      });

    LIBRARY_MUTATIONS.forEach(thunk => {
      builder
        .addCase(thunk.pending, state => {
          state.librarySaving = true;
        })
        .addCase(thunk.fulfilled, state => {
          state.librarySaving = false;
        })
        .addCase(thunk.rejected, state => {
          state.librarySaving = false;
        });
    });
  },
});

export default videoSlice.reducer;
